import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { FiCheckCircle, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import api from 'services/api';
import { Role } from 'utils/variables';
import { show } from 'store/modules/dialog/actions';
import AvailableImg from 'assets/list.svg';
import AppError from 'utils/AppError';

import LoadingTable from 'components/LoadingTable';
import Card from 'components/Card';
import Button from 'components/Button';
import NotFound from 'components/NotFound';

import { Scroll, Table } from 'styles/global';

import { Emissora, Usuario } from 'pages/@types';
import Pagination from 'components/Pagination';
import Tooltip from 'components/Tooltip';
import Avatar from 'components/Avatar';

interface ListaExclusao {
  id: number;
  filename: string;
  created_at: string;
  operadorcta?: Usuario;
  operadorlda?: Usuario;
  operadorfoz?: Usuario;
  operadorcvl?: Usuario;
  operadormga?: Usuario;
  operadorpvi?: Usuario;
  operadorgua?: Usuario;
  operadorpgo?: Usuario;
}

interface EmissoraComOperador extends Emissora {
  operador:
    | 'operadorcta'
    | 'operadorlda'
    | 'operadorfoz'
    | 'operadorcvl'
    | 'operadormga'
    | 'operadorpvi'
    | 'operadorgua'
    | 'operadorpgo';
}

export default function Exclusao() {
  // react-router-dom
  const navigate = useNavigate();

  const { profile } = useSelector((state: any) => state.usuario);
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  const dispatch = useDispatch();
  const [listasExclusao, setListasExclusao] = useState<ListaExclusao[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [selectedLE, setSelectedLE] = useState({});
  const [selectedEmissora, setSelectedEmissora] = useState<EmissoraComOperador>(
    {} as EmissoraComOperador,
  );
  const [emissoras, setEmissoras] = useState<EmissoraComOperador[]>([]);

  const handleLE = async (l: ListaExclusao, praca: EmissoraComOperador) => {
    setSelectedLE(l);
    setSelectedEmissora(praca);
    try {
      const option = {
        type: 'question',
        text: `Confirmar execução de ${l.filename}?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          data: {
            praca: `${praca.operador}_id`,
          },
          url: `/listasexclusao/${l.id}`,
          method: api.put,
        },
        haveCallback: true,
        successMessage: 'Confirmado execução!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(option));
    } catch (err) {}
  };

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'success' && selectedLE) {
        const listaExecutada = callbackDialog.success.data.listaExclusao;
        const operadorInfo = {
          avatar_url: profile.avatar_url,
          nome: profile.nome,
          login: profile.login,
          avatar: profile.avatar,
        };
        if (listaExecutada) {
          setListasExclusao(prevState =>
            prevState.map(l =>
              l.id === listaExecutada.id
                ? {
                    ...l,
                    [selectedEmissora.operador]: operadorInfo,
                  }
                : l,
            ),
          );
        }
      }
    }
  }, [callbackDialog, profile, selectedEmissora.operador, selectedLE]);

  const operador = useCallback((siglapromo: string) => {
    switch (siglapromo) {
      case 'CTA':
        return 'operadorcta';
      case 'LDA':
        return 'operadorlda';
      case 'CAS':
        return 'operadorcvl';
      case 'FOZ':
        return 'operadorfoz';
      case 'MGA':
        return 'operadormga';
      case 'PVI':
        return 'operadorpvi';
      case 'GUA':
        return 'operadorgua';
      default:
        return 'operadorpgo';
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function loadListas() {
      try {
        const [responseListaExclusao, responseEmissoras] = await Promise.all([
          api.get('/listaexclusao', {
            params: { page },
            signal: controller.signal,
          }),
          api.get('/emissoras', {
            signal: controller.signal,
          }),
        ]);
        if (
          responseListaExclusao &&
          responseListaExclusao.data &&
          responseEmissoras &&
          responseEmissoras.data
        ) {
          const [listas, pages] = responseListaExclusao.data;
          setListasExclusao(listas);
          setPages(pages);

          setEmissoras(
            responseEmissoras.data.map((p: Emissora) => ({
              ...p,
              operador: operador(p.siglapromo),
            })),
          );
        } else toast.error('Falha de comunicação com o sistema');
        setLoading(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
        setLoading(false);
      }
    }
    setLoading(true);
    loadListas();
    return () => {
      controller.abort();
    };
  }, [operador, page]);
  return (
    <Card height="full" padding="15px">
      {listasExclusao.length > 0 && !loading && (
        <>
          <header>
            <h4>LISTA DE EXCLUSÃO</h4>
            <Pagination
              total={pages}
              activePage={page}
              onClick={page => setPage(page)}
            />
            <div />
          </header>
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th>NOME DA LISTA</th>
                  {emissoras.map(e => (
                    <th style={{ width: 70 }} key={`th${e.id}`}>
                      {e.siglapromo}
                    </th>
                  ))}
                  <th style={{ width: 200, textAlign: 'center' }}>GERADA EM</th>
                  <th style={{ width: 60 }}>IDS</th>
                </tr>
              </thead>

              <tbody>
                {!!listasExclusao &&
                  listasExclusao.map(p => (
                    <tr key={p.id}>
                      <td>{p.filename}</td>
                      {emissoras.map(e => (
                        <td key={`td${e.id}_${p.id}`}>
                          {p[e.operador] ? (
                            <Avatar
                              size="40"
                              name={p[e.operador]?.nome}
                              src={p[e.operador]?.avatar_url}
                              round
                            />
                          ) : (
                            <Button
                              fill
                              color="font"
                              round
                              disabled={
                                !(
                                  (profile.emissoras?.some(
                                    (em: Emissora) => em.sigla === e.sigla,
                                  ) &&
                                    profile.role === Role.EXIB) ||
                                  profile.role === Role.ADMIN
                                )
                              }
                              tooltip="Confirmar Execução"
                              onClick={() => {
                                handleLE(p, e);
                              }}
                            >
                              <FiCheckCircle />
                            </Button>
                          )}
                        </td>
                      ))}
                      <td style={{ textAlign: 'center' }}>
                        {format(new Date(p.created_at), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td>
                        <Button
                          id={`id_visualizar_${p.id}`}
                          fill
                          color="font"
                          round
                          onClick={() => navigate(`/exclusao/${p.id}`)}
                        >
                          <FiInfo />
                        </Button>
                        <Tooltip
                          target={`id_visualizar_${p.id}`}
                          position="left"
                        >
                          VISUALIZAR MIDIAS
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Scroll>
        </>
      )}

      {listasExclusao.length <= 0 && !loading && (
        <>
          <h4>LISTAS DE EXCLUSÃO</h4>
          <NotFound img={AvailableImg} title="Lista de exclusão">
            Nenhuma lista <b>gerada</b>
          </NotFound>
        </>
      )}
      {loading && <LoadingTable type="" />}
    </Card>
  );
}
